// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useState, useEffect, useCallback } from 'react'
import { formatUsDate } from '../../api/format'
import { Loader2, RefreshCw, AlarmClock, Check, X, RotateCcw, AlertTriangle } from 'lucide-react'
import * as api from '../../api/client'
import { rowOpen, rowActions } from './rowOpen'
import CareRxClinicalReviewDetail from './CareRxClinicalReviewDetail'
import { friendlyError } from '../../api/errors'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../../components/Pager'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'
import PatientLanguageBadge from '../../components/ui/PatientLanguageBadge'
import { useRefreshClinicalCounts } from '../../hooks/queries/carerxClinical'

// D-UI-2: a funnel on every column of the renewal queue. Values are what the cell shows.
const RENEWAL_FUNNELS: FunnelColumn<api.CareRxRenewalRow>[] = [
  { key: 'patient', label: 'Patient', value: it => it.patient_name || '—' },
  { key: 'trigger', label: 'Trigger', value: it => String(it.trigger_type ?? '').toUpperCase() },
  { key: 'drug', label: 'Drug', value: it => it.drug_name || '—' },
  { key: 'cycle', label: 'Cycle', value: it => it.cycle_no ?? '—' },
  { key: 'due', label: 'Due', value: it => it.due_date },
  { key: 'status', label: 'Status', value: it => String(it.status ?? '').replace(/\b\w/g, c => c.toUpperCase()) },
]

/**
 * D4 Renewal queue — the human-facing view of the D3 re-careplan engine. "Run scan" calls
 * /carerx/recareplan/run to (re)generate auto triggers; rows can be dismissed / resolved / reopened;
 * a manual trigger can be added for events with no structured signal (new dx, infusion reaction,
 * hospitalization). Auto rows are regenerated each scan; manual + actioned rows persist.
 */
const STATUS_TABS = ['open', 'dismissed', 'resolved', 'all']

export default function CareRxRenewals() {
  const [items, setItems] = useState<api.CareRxRenewalRow[]>([])
  // Same enrollment record as Review and Hold: a renewal is a stage of the therapy, not a
  // separate thing, so you can open it and read the labs before deciding.
  const [openTherapy, setOpenTherapy] = useState<string | null>(null)
  const [status, setStatus] = useState('open')
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')
  const [running, setRunning] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  // Paginate so a large renewal queue never renders thousands of rows at once.
  const fx = useTableFunnels(items, RENEWAL_FUNNELS)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(fx.rows, DEFAULT_PAGE_SIZE, `${status}|${fx.sig}`)

  // ASP #23: every reload refreshes the stage counts — an action here may have moved a therapy on.
  const refreshCounts = useRefreshClinicalCounts()
  const load = useCallback(async () => {
    setLoading(true); setMsg('')
    refreshCounts()
    try { const d = await api.listCareRxRecareplanQueue({ status }); setItems(d.items ?? []) }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setLoading(false) }
  }, [status, refreshCounts])

  useEffect(() => { load() }, [load])

  const runScan = async () => {
    setRunning(true); setMsg('')
    try {
      const d = await api.runCareRxRecareplan()
      const parts = Object.entries(d.by_type || {}).map(([k, v]) => `${v} ${k}`).join(', ')
      setMsg(d.note ? d.note : `Scanned ${d.scanned_patients} patient(s) — ${d.triggers} trigger(s)${parts ? ` (${parts})` : ''}.`)
      await load()
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setRunning(false) }
  }

  const act = async (id: string, action: 'dismiss' | 'resolve' | 'reopen') => {
    setBusyId(id); setMsg('')
    try { await api.updateCareRxRecareplanItem(id, action); await load() }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusyId(null) }
  }


  if (openTherapy) {
    return <CareRxClinicalReviewDetail therapyId={openTherapy} backLabel="Back to Renewals"
      onBack={() => { setOpenTherapy(null); void load() }} />
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <div style={cardTitle}><AlarmClock size={16} /> Re-careplan renewals</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button style={primaryBtn} disabled={running} onClick={runScan}>
            {running ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />} Run scan
          </button>
        </div>
      </div>
      <p style={{ ...muted, marginTop: -8 }}>
        Patients whose plan of care is due for renewal. "Run scan" regenerates auto triggers from the formulary interval; actioned rows persist. Events with no structured signal (new diagnosis, infusion reaction, hospitalization) are put on hold from the Hold tab.
      </p>


      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {STATUS_TABS.map(s => (
          <button key={s} onClick={() => setStatus(s)} style={{
            padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: '1px solid var(--color-border)', textTransform: 'capitalize',
            background: status === s ? 'var(--color-primary)' : 'var(--color-surface)',
            color: status === s ? '#fff' : 'var(--color-text)',
          }}>{s}</button>
        ))}
      </div>

      {msg && <div style={{ fontSize: 13, color: 'var(--color-text-mute)' }}>{msg}</div>}

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ ...muted, padding: 20 }}><Loader2 size={15} className="spin" /> Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ ...muted, padding: 20 }}>No {status === 'all' ? '' : status} renewals. Try "Run scan".</div>
        ) : (
          <>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="renewals" />
          {fx.chips}
          <table {...fx.tableProps} style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Patient {fx.funnel('patient')}</th>
                <th style={th}>Trigger {fx.funnel('trigger')}</th>
                <th style={th}>Drug {fx.funnel('drug')}</th>
                <th style={th}>Cycle {fx.funnel('cycle')}</th>
                <th style={th}>Due {fx.funnel('due')}</th>
                <th style={th}>Status {fx.funnel('status')}</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map(it => (
                <tr key={it.id} {...rowOpen(() => it.therapy_id && setOpenTherapy(it.therapy_id), { borderBottom: '1px solid var(--color-border)' })}>
                  <td data-label="Patient" style={td}>
                    <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                      {it.severity === 'high' && <AlertTriangle size={13} color="#b45309" />}{it.patient_name || '—'}<PatientLanguageBadge patientId={it.patient_id} />
                    </div>
                  </td>
                  <td data-label="Trigger" style={td}>
                    <span style={typeBadge(it.trigger_type)}>{it.trigger_type}</span>
                    <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginTop: 2 }}>{it.trigger_reason}</div>
                    {it.source === 'manual' && <span style={{ fontSize: 10, color: 'var(--color-text-mute)' }}>manual</span>}
                  </td>
                  <td data-label="Drug" style={td}>{it.drug_name || '—'}
                    {it.open_exception && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#b91c1c' }}>RISK: open exception</span>}
                    {it.labs_expiring_30d && <span style={{ marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#b45309' }}>labs expire ≤30d</span>}
                  </td>
                  <td data-label="Cycle" style={td}>{it.cycle_no ?? '—'}</td>
                  <td data-label="Due" style={td}>{formatUsDate(it.due_date)}</td>
                  <td data-label="Status" style={td}><span style={statusPill(it.status)}>{it.status}</span></td>
                  <td data-label="" style={td} {...rowActions}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {it.status === 'open' ? (
                        <>
                          {it.therapy_id && <button style={actBtn('var(--color-primary)')} title="Open the enrollment record"
                            onClick={() => setOpenTherapy(it.therapy_id!)}>Open</button>}
                          <button style={actBtn('#15803d')} disabled={busyId === it.id} onClick={() => act(it.id, 'resolve')}><Check size={13} /> Resolve</button>
                          <button style={actBtn('#6b7280')} disabled={busyId === it.id} onClick={() => act(it.id, 'dismiss')}><X size={13} /> Dismiss</button>
                        </>
                      ) : (
                        <button style={actBtn('var(--color-primary)')} disabled={busyId === it.id} onClick={() => act(it.id, 'reopen')}><RotateCcw size={13} /> Reopen</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="renewals" />
          </>
        )}
      </div>
      {!loading && items.length > 0 && <div style={muted}>{items.length} item{items.length === 1 ? '' : 's'}</div>}
    </div>
  )
}

const card: React.CSSProperties = { border: '1px solid var(--color-border)', borderRadius: 12, padding: 14, background: 'var(--color-surface)' }
const cardTitle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700 }
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: .4, color: 'var(--color-text-mute)', borderBottom: '2px solid var(--color-border)' }
const td: React.CSSProperties = { padding: '9px 10px', fontSize: 13, verticalAlign: 'top' }
const muted: React.CSSProperties = { fontSize: 12, color: 'var(--color-text-mute)', display: 'flex', alignItems: 'center', gap: 6 }
const primaryBtn: React.CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const actBtn = (color: string): React.CSSProperties => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: `1px solid ${color}`, background: 'var(--color-surface)', color })
const typeBadge = (t: string): React.CSSProperties => ({ display: 'inline-block', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: .3, color: t === 'event' ? '#b45309' : t === 'manual' ? '#7c3aed' : '#0369a1' })
const statusPill = (s: string): React.CSSProperties => ({ display: 'inline-block', fontSize: 11, fontWeight: 600, textTransform: 'capitalize', borderRadius: 999, padding: '2px 9px', border: '1px solid var(--color-border)', color: s === 'open' ? '#0369a1' : s === 'resolved' ? '#15803d' : 'var(--color-text-mute)' })
